import { useRef } from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { Placeholder } from 'rsuite'
import useTranslation from '@/hooks/useTranslation'
import useImageLoaded from '@/hooks/useImageLoaded'
import { RgbCircle, WhiteCircle, TransparentCircle } from '@/components/shared/hotspot'
import styles from './overview.module.scss'

const ImageHotspots = dynamic(() => import('react-image-hotspots'), { ssr: false })

const HOTSPOT_COMPONENTS = {
  rgb: RgbCircle,
  white: WhiteCircle,
  transparent: TransparentCircle
}

/**
 * OverviewImage Component
 * Displays a vehicle image with interactive hotspots
 *
 * @param {Object} hotspotForCurrentView - Hotspot data for current view
 * @param {string} selectedPart - Currently selected hotspot
 * @param {Function} onHotSpotClick - Callback when hotspot is clicked
 * @param {string} variant - Hotspot variant: 'rgb', 'white', or 'transparent' (preferred)
 * @param {string} altImage - Alt text for the image for accessibility
 * @param {boolean} rgb - @deprecated Use 'variant' prop instead. Legacy prop for backward compatibility
 */
const OverviewImage = ({ hotspotForCurrentView, selectedPart, onHotSpotClick, variant, altImage }) => {
  const t = useTranslation()
  const HotspotComponent = HOTSPOT_COMPONENTS[variant] || RgbCircle
  const containerRef = useRef(null)
  const imageLoaded = useImageLoaded(containerRef, hotspotForCurrentView?.photo)

  return (
    <div className={styles.overviewImage}>
      {hotspotForCurrentView?.photo ? (
        <div ref={containerRef} className={styles.imageContainer}>
          {!imageLoaded && (
            <div className={styles.loadingOverlay}>
              <Placeholder.Graph width={500} height={316} active />
            </div>
          )}
          <div className={`${styles.imageWrapper} ${imageLoaded ? styles.loaded : styles.loading}`}>
            <ImageHotspots
              src={`${hotspotForCurrentView.photo}?noavif`}
              alt={altImage}
              hideFullscreenControl
              hideMinimap
              hideZoomControls
              hotspots={hotspotForCurrentView.spots?.map(({ x, y, tag, publication }) => {
                const value = tag || publication
                const translatedValue = t(`tag-${value.toUpperCase()}`, value)
                const ariaLabel = `${t('view_topics_for', 'View topics for')}: ${translatedValue}`
                const circleProps = {
                  $isActive: value === selectedPart,
                  onClick: () => onHotSpotClick(value),
                  ariaLabel,
                  title: ariaLabel
                }
                return {
                  x,
                  y,
                  content: <HotspotComponent {...circleProps} />,
                  style: { background: 'none', pointerEvents: 'all' }
                }
              })}
            />
          </div>
        </div>
      ) : (
        <Image
          src="/images/noImage.jpg"
          alt={t('no_image', 'No image')}
          width={500}
          height={316}
          className={styles.noImageStyle}
        />
      )}
    </div>
  )
}

export default OverviewImage
